// Libraries
import React from 'react';

// Supermove
import {Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {useDrawer, useResponsive} from '@supermove/hooks';
import {ProjectModel} from '@supermove/models';
import {colors} from '@supermove/styles';

// App
import FieldValue from '@shared/design/components/Field/FieldValue';
import SkeletonLoader from '@shared/design/components/SkeletonLoader';
import Text from '@shared/design/components/Text';
import EditProjectReferralInformationDrawer from '@shared/modules/Project/components/widgets/components/EditProjectReferralInformationDrawer';
import ProjectDetailsBlockHeader, {
  BlockHeaderLoader,
  EditButton,
} from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockHeader';
import ProjectDetailsBlockWrapper from '@shared/modules/Project/components/widgets/components/ProjectDetailsBlockWrapper';

const Column = Styled.View`
`;

const SkeletonField = ({label}: {label: string}) => {
  const responsive = useResponsive();

  return (
    <Column style={{flex: 1}}>
      <Text.Label style={{color: colors.gray.secondary}}>{label}</Text.Label>
      <FieldValue.LabelSpace isResponsive responsive={responsive} />
      <SkeletonLoader
        height={
          responsive.desktop
            ? SkeletonLoader.HEIGHT.Typography.Body
            : SkeletonLoader.HEIGHT.Typography.Subheading
        }
        width={200}
      />
    </Column>
  );
};

const SkeletonComponent = () => {
  return (
    <Column>
      <BlockHeaderLoader
        title={'Referral Information'}
        ActionsComponent={() => <EditButton isDisabled />}
      />
      <SkeletonField label={'How did you hear about us?'} />
      <Space height={16} />
      <SkeletonField label={'Referral Details'} />
      <Space height={16} />
    </Column>
  );
};

const ReferralInformationFields = ({project}: {project: ProjectModel}) => {
  return (
    <React.Fragment>
      <FieldValue label={'How did you hear about us?'} value={project.referralSource} />
      <Space height={16} />
      <FieldValue label={'Referral Details'} value={project.referralDetails} />
      <Space height={16} />
    </React.Fragment>
  );
};

const EditReferralInformationButton = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  const editReferralInformationDrawer = useDrawer({
    name: 'Edit Project Referral Information Drawer',
  });

  return (
    <React.Fragment>
      <EditButton onPress={editReferralInformationDrawer.handleOpen} />
      <EditProjectReferralInformationDrawer
        key={editReferralInformationDrawer.key}
        project={project}
        isOpen={editReferralInformationDrawer.isOpen}
        handleClose={editReferralInformationDrawer.handleClose}
        refetch={refetch}
      />
    </React.Fragment>
  );
};

const ProjectDetailsReferralInformationBlockContentMobile = ({
  project,
  refetch,
}: {
  project: ProjectModel;
  refetch: () => void;
}) => {
  return (
    <React.Fragment>
      <ProjectDetailsBlockHeader
        title={`Referral Information`}
        ActionsComponent={EditReferralInformationButton}
        actionsComponentProps={{project, refetch}}
      />
      <ReferralInformationFields project={project} />
    </React.Fragment>
  );
};

const ProjectDetailsReferralInformationBlock = ({
  projectUuid,
  index,
}: {
  projectUuid: string;
  index: number;
}) => {
  return (
    <ProjectDetailsBlockWrapper
      index={index}
      query={ProjectDetailsReferralInformationBlock.query}
      queryVariables={{projectUuid}}
      SkeletonComponent={SkeletonComponent}
    >
      {({data, refetch}: {data: {project: ProjectModel}; refetch: () => void}) => {
        return (
          <ProjectDetailsReferralInformationBlockContentMobile
            project={data.project}
            refetch={refetch}
          />
        );
      }}
    </ProjectDetailsBlockWrapper>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectDetailsReferralInformationBlock.query = gql`
  ${EditProjectReferralInformationDrawer.fragment}

  query ProjectDetailsReferralInformationBlock($projectUuid: String!) {
    ${gql.query}
    project(uuid: $projectUuid) {
      id
      referralSource
      referralDetails
      organization {
        id
        features {
          isEnabledProjectReferralSourceRequired: isEnabled(
            feature: "PROJECT_REFERRAL_SOURCE_REQUIRED"
          )
        }
      }
      ...EditProjectReferralInformationDrawer
    }
  }
`;

export default ProjectDetailsReferralInformationBlock;
